import { Field } from 'formik';
import React from 'react';
import { MiniAppFocusWrapper, RadioButtonGroup } from 'src/components';
import { GeneratePaymentRequestWizardPageProps } from 'src/containers/self-service/generate-payment-request/components/GeneratePaymentRequestWizard';
import {
  NotificationTypeRadioButton,
  tagOnChange,
} from 'src/containers/self-service/generate-payment-request/components/NotificationTypeRadioButton';
import {
  TextInput,
  tagOnFocus,
} from 'src/containers/self-service/generate-payment-request/components/TextInput';
import {
  LinkName,
  PageName,
  PaymentRequestTealiumService,
  UIInteractionType,
} from 'src/containers/self-service/generate-payment-request/tagging/payment-request-tealium-service';
import { phoneNumberMaskSpaced, centInputMask } from 'src/helpers/formatting';
import { NotificationMethod, PaymentType } from 'src/services/payment-request-service';

const getPageName = (paymentType: PaymentType): PageName => {
  return paymentType === PaymentType.FixedAmount
    ? PageName.GENERATE_PAYMENT_REQUEST_DETAILS_FIXED
    : PageName.GENERATE_PAYMENT_REQUEST_DETAILS_VARIABLE;
};

export function PaymentRequestDetails(props: GeneratePaymentRequestWizardPageProps) {
  React.useEffect(() => {
    props.setTouched({});

    props.formValues.categoryType === PaymentType.FixedAmount &&
      PaymentRequestTealiumService.onPaymentRequestFixedStart();
    props.formValues.categoryType === PaymentType.VariableAmount &&
      PaymentRequestTealiumService.onPaymentRequestVariableStart();

    props.setOnNextEventHandler(() => {
      PaymentRequestTealiumService.onUIInteraction(
        LinkName.GENERATE_PAYMENT_REQUEST,
        `${
          UIInteractionType[props.formValues.categoryType] as UIInteractionType
        }: payment request details: ${UIInteractionType.Next}`,
        UIInteractionType[props.formValues.categoryType],
      );
    });

    props.setOnBackEventHandler(() => {
      PaymentRequestTealiumService.onBackButton(getPageName(props.formValues.categoryType));
    });
  }, []);

  return (
    <div className="p-6 mb-10">
      <section className="md:text-center">
        <h1 className="text-base font-medium text-grey-100 md:text-4xl">Payment request details</h1>

        <h3 className="mt-2 text-xs md:text-base text-white-50">
          Please provide the contact details of the person you wish to request payment from.
        </h3>
      </section>
      <section className="flex flex-col items-center mt-4 md:mt-14">
        <div className="flex flex-col w-full gap-8 md:w-130">
          {props.formValues.categoryType === PaymentType.FixedAmount && (
            <>
              <Field
                name={'transactionReference'}
                type="text"
                placeholder="Enter transaction reference"
                component={MiniAppFocusWrapper}
                innerComponent={TextInput}
                maxlength="50"
                fieldType="standard"
                tooltip="Capture a unique reference to assist you in reconciling this transaction when payment is made."
                className="dark"
                automationid="Revamp_Your_Details_First_Name"
                onBlurred={() =>
                  PaymentRequestTealiumService.onFilledFormInput(
                    props.formValues.transactionReference,
                  )
                }
              />

              <div className="relative [&_label[for=transactionAmount]]:!pl-5">
                <p className={`absolute top-[27px] left-4 z-10 text-white text-base`}>R</p>
                <Field
                  name={'transactionAmount'}
                  type="text"
                  placeholder={props.formValues.transactionAmount ? '' : '0.00'}
                  component={MiniAppFocusWrapper}
                  innerComponent={TextInput}
                  mask={centInputMask}
                  onBlurred={() =>
                    PaymentRequestTealiumService.onFilledFormInput(
                      props.formValues.transactionAmount,
                    )
                  }
                  fieldType="masked"
                  className="dark [&_input]:!pl-9"
                  automationid="Revamp_Your_Details_First_Name"
                />
              </div>
            </>
          )}
          <div className="flex flex-col gap-4 [&_.columns.is-centered]:gap-3 [&_.columns.is-centered]:justify-start [&_.column]:basis-auto [&_.column]:flex-grow-0">
            <p>Notification Type</p>
            <RadioButtonGroup
              className="bank-account-type-radio-buttons"
              automationId="Onboarding_Create_Account_Your_Details_Radio_Button_Group_Account_Type"
            >
              <Field
                type="radio"
                name="notificationMethod"
                value={NotificationMethod.Email}
                label={'Email'}
                autoFocus={true}
                onFocus={() => {
                  tagOnChange(NotificationMethod.Email, props.formValues.categoryType);
                }}
                clearField="notificationAddress"
                paymentType={props.formValues.categoryType}
                notificationAddress={props.formValues.notificationAddress}
                component={NotificationTypeRadioButton}
                automationId="Onboarding_Create_Account_Your_Details_Radio_Button_Group_Account_Type_Value_Personal"
              />
              <Field
                type="radio"
                name="notificationMethod"
                value={NotificationMethod.SMS}
                label={'SMS'}
                clearField="notificationAddress"
                paymentType={props.formValues.categoryType}
                component={NotificationTypeRadioButton}
                notificationAddress={props.formValues.notificationAddress}
                automationId="Onboarding_Create_Account_Your_Details_Radio_Button_Group_Account_Type_Value_Business"
              />
            </RadioButtonGroup>
          </div>

          <Field
            name="notificationAddress"
            type={
              props.formValues.notificationMethod === NotificationMethod.Email ? 'email' : 'tel'
            }
            placeholder={
              props.formValues.notificationMethod === NotificationMethod.Email
                ? 'Enter Email'
                : 'Mobile Number'
            }
            component={MiniAppFocusWrapper}
            onFocus={() => {
              tagOnFocus(props.formValues.notificationMethod, props.formValues.categoryType);
            }}
            innerComponent={TextInput}
            fieldType={
              props.formValues.notificationMethod === NotificationMethod.SMS ? 'masked' : 'standard'
            }
            onValidationError={() => {
              const errorName = `please enter a valid ${
                props.formValues.notificationMethod === NotificationMethod.Email
                  ? UIInteractionType.Email
                  : UIInteractionType.PhoneNumber
              }`;
              const errorCode = `invalid  ${
                props.formValues.notificationMethod === NotificationMethod.Email
                  ? UIInteractionType.EmailAddress
                  : UIInteractionType.PhoneNumber
              }`;
              PaymentRequestTealiumService.onFormError(
                props.formValues.categoryType,
                errorName,
                errorCode,
              );
            }}
            className="dark"
            mask={phoneNumberMaskSpaced}
            onBlurred={() =>
              PaymentRequestTealiumService.onFilledFormInput(props.formValues.notificationAddress)
            }
            automationid="Revamp_Your_Details_First_Name"
          />
        </div>
      </section>
    </div>
  );
}
